import { QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { AcceptedIntakeAbortionCounts, GTPALCountValue } from "@/clients/interfaces/gtpal";
import { PastPregnanciesCodes } from "@/fhir/enums/quilted-health";
import { getAcceptedIntegerAnswer } from "@/utils/medplum";

/**
 * Derives the GTPAL "A" figure from its two components.
 *
 * A component left blank in the drawer keeps its accepted intake answer, so the total stays equal
 * to the intake's own two answers. A component with no accepted answer anywhere counts as zero.
 *
 * @param abortionsInduced The induced abortion count entered in the drawer, if any.
 * @param miscarriages The miscarriage count entered in the drawer, if any.
 * @param intakeAbortionCounts The client's accepted intake counts for each component.
 * @returns The combined count.
 */
export function deriveAbortionsTotal(
  abortionsInduced: number | undefined,
  miscarriages: number | undefined,
  intakeAbortionCounts: AcceptedIntakeAbortionCounts,
): number {
  return (
    (abortionsInduced ?? intakeAbortionCounts.abortionsInduced ?? 0) +
    (miscarriages ?? intakeAbortionCounts.miscarriages ?? 0)
  );
}

/**
 * Sums the drawer's two abortion fields into the GTPAL "A" figure. A blank field counts as zero.
 *
 * @param abortionsInduced The raw drawer value for induced abortions.
 * @param miscarriages The raw drawer value for miscarriages.
 * @returns The combined count.
 */
export function sumAbortionCounts(
  abortionsInduced: GTPALCountValue,
  miscarriages: GTPALCountValue,
): number {
  return (toOptionalCount(abortionsInduced) ?? 0) + (toOptionalCount(miscarriages) ?? 0);
}

/**
 * Reads the accepted induced-abortions and miscarriages answers from an intake response.
 *
 * @param intakeItems The intake response's items, if the client has one.
 * @returns Each accepted count, or undefined where the answer is missing or not accepted.
 */
export function getAcceptedIntakeAbortionCounts(
  intakeItems: QuestionnaireResponseItem[] | undefined,
): AcceptedIntakeAbortionCounts {
  return {
    abortionsInduced: getAcceptedIntegerAnswer(intakeItems, PastPregnanciesCodes.NInducedAbortions),
    miscarriages: getAcceptedIntegerAnswer(intakeItems, PastPregnanciesCodes.NMiscarriages),
  };
}

/**
 * Reads a raw drawer count field as a number.
 *
 * An untouched Mantine number input holds an empty string, so the raw value is coerced.
 *
 * @param value The raw form value for one count field.
 * @returns The count, or undefined when the field is empty.
 */
function toOptionalCount(value: GTPALCountValue): number | undefined {
  if (value === undefined || value === "") return undefined;

  const count = Number(value);
  return Number.isFinite(count) ? count : undefined;
}
